import { tripEmploymentSelection } from '../tripEmploymentSelection'
import { TripEmploymentField, type TripEmploymentChoice } from './TripEmploymentField'
import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useEffect, useMemo, useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../../i18n'
import { tone } from '../../../theme/tones'
import { Avatar, Chip, DateField, FormInput, FormTextArea, Icon, OptionSelect } from '../../../ui'
import { DATE_RE, ymdPlusDays } from '../../../lib/date'
import { daysInclusive } from '../../../lib/format'
import { useMe } from '../../users/useUsers'
import { useUserSearch } from '../../users/useUserSearch'
import { useCreateBusinessTrip, useTripDestinations } from '../useEdo'
import { RoutePreview } from '../components/RouteTimeline'
import { Field, FormActions, MISSING_DATE_FROM, MISSING_DATE_TO, MISSING_WORKPLACE, dateCol } from './shared'

// ── Business trip (ТЗ-2026-003) ─────────────────────────────────────────────────
type Traveller = { id: string; fullName: string; jobTitle?: string | null }

/**
 * Участники поездки (групповая командировка). Список отправляется целиком
 * (employeeIds = полный состав): по умолчанию — вы; себя можно убрать, если
 * оформляете поездку коллегам. Поиск — как ManagerPicker (debounce + /users?search).
 */
function TripMatesPicker({
  travellers,
  onChange,
}: {
  travellers: Traveller[]
  onChange: (next: Traveller[]) => void
}) {
  const { t } = useApp()
  const { q, setQ, debounced, hits } = useUserSearch()
  const chosen = new Set(travellers.map((m) => m.id))
  const options = useMemo(() => (hits ?? []).filter((u) => !chosen.has(u.id)).slice(0, 6), [hits, travellers])

  return (
    <YStack gap={8}>
      {/* выбранные — чипы с аватаром и крестиком */}
      <XStack gap={8} flexWrap="wrap">
        {travellers.map((m) => (
          <XStack
            key={m.id}
            alignItems="center"
            gap={7}
            backgroundColor="$bgLayout"
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius={999}
            paddingLeft={5}
            paddingRight={9}
            paddingVertical={4}
          >
            <Avatar name={m.fullName} seed={m.id} size="sm" />
            <Text fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
              {m.fullName}
            </Text>
            <Stack onPress={() => onChange(travellers.filter((x) => x.id !== m.id))} cursor="pointer" padding={2}>
              <Icon name="x" size={13} color="$muted" />
            </Stack>
          </XStack>
        ))}
        {travellers.length === 0 && (
          <Text fontSize={12} color={tone('red').text}>
            {t({ ru: 'Добавьте хотя бы одного участника', en: 'Add at least one traveller' })}
          </Text>
        )}
      </XStack>

      <FormInput
        value={q}
        onChangeText={setQ}
        placeholder={t({ ru: 'Добавить коллегу (поиск по имени)…', en: 'Add a colleague (search by name)…' })}
      />
      {debounced.length >= 2 && options.length > 0 && (
        <YStack borderWidth={1} borderColor="$borderSecondary" borderRadius={7} overflow="hidden">
          {options.map((u, i) => (
            <XStack
              key={u.id}
              onPress={() => {
                onChange([...travellers, { id: u.id, fullName: u.fullName, jobTitle: u.jobTitle }])
                setQ('')
              }}
              cursor="pointer"
              alignItems="center"
              gap={8}
              padding={8}
              borderTopWidth={i === 0 ? 0 : 1}
              borderColor="$borderSecondary"
              hoverStyle={{ backgroundColor: '$backgroundHover' }}
            >
              <Avatar name={u.fullName} seed={u.id} size="sm" />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={13} color="$color" numberOfLines={1}>
                  {u.fullName}
                </Text>
                {!!u.jobTitle && (
                  <Text fontSize={11} color="$muted" numberOfLines={1}>
                    {u.jobTitle}
                  </Text>
                )}
              </YStack>
              <Icon name="plus" size={14} color="$primary" />
            </XStack>
          ))}
        </YStack>
      )}
    </YStack>
  )
}

/**
 * Направление командировки: страна — выпадающий список с поиском (весь справочник норм 1С
 * по алфавиту), город — норм-города чипами / свой ввод. Названия стран в 1С азербайджанские
 * («Gürcüstan»): поиск в списке понимает их без диакритики, а сам список виден целиком.
 */
type TripDest = { stranaId: string; country: string; city: string; perDiem: number; currency: string }
function TripDestinationPicker({
  value,
  onChange,
}: {
  value: TripDest | null
  onChange: (v: TripDest | null) => void
}) {
  const { t } = useApp()
  const { data: dests } = useTripDestinations()
  const list = dests ?? []
  const selected = value?.stranaId ? list.find((d) => d.stranaId === value.stranaId) ?? null : null
  const countries = useMemo(
    () =>
      [...(dests ?? [])]
        .sort((a, b) => a.country.localeCompare(b.country, 'az'))
        .map((d) => ({ value: d.stranaId, label: d.country })),
    [dests],
  )

  return (
    <YStack gap={8}>
      <OptionSelect
        list
        searchable
        options={countries}
        value={selected ? [selected.stranaId] : []}
        onChange={([id]) => {
          const d = list.find((x) => x.stranaId === id)
          // Другая страна — город выбирается заново; повторный выбор той же город не сбрасывает.
          if (d && d.stranaId !== value?.stranaId) onChange({ stranaId: d.stranaId, country: d.country, city: '', perDiem: 0, currency: '' })
        }}
        placeholder={t({ ru: 'Выберите страну', en: 'Choose a country', az: 'Ölkəni seçin' })}
        highlight={!selected}
      />

      {/* город: норм-города чипами (несут суточную) + свой ввод для прочих городов */}
      {selected && (
        <YStack gap={6}>
          <XStack gap={6} flexWrap="wrap">
            {/* Паддинги переопределены: у города чип на 1px компактнее дефолтного Chip. */}
            {selected.cities.map((c) => (
              <Chip
                key={c.city}
                on={c.city === value?.city}
                dim
                onPress={() => onChange({ stranaId: selected.stranaId, country: selected.country, city: c.city, perDiem: c.perDiem, currency: c.currency })}
                paddingHorizontal={12}
                paddingVertical={6}
              >
                {c.city}
              </Chip>
            ))}
          </XStack>
          <FormInput
            value={value?.city ?? ''}
            onChangeText={(v) => {
              const norm = selected.cities.find((c) => c.city === v)
              onChange({ stranaId: selected.stranaId, country: selected.country, city: v, perDiem: norm?.perDiem ?? 0, currency: norm?.currency ?? '' })
            }}
            maxLength={200}
            placeholder={t({ ru: 'Город (или свой, если нет в списке)', en: 'City (or type your own)' })}
          />
        </YStack>
      )}
    </YStack>
  )
}

export function BusinessTripForm({ onDone }: { onDone: () => void }) {
  const employment = useEmploymentSelection()
  const { t } = useApp()
  const { data: me } = useMe()
  const create = useCreateBusinessTrip(onDone)

  const [travellers, setTravellers] = useState<Traveller[]>([])
  const [targetChoices, setTargetChoices] = useState<Record<string, TripEmploymentChoice & { scope: string }>>({})
  const companyId = employment.jobs.find(job => job.id === employment.employmentId)?.companyId
  const scope = `${employment.employmentId ?? ''}:${travellers.map(user => user.id).join(',')}`
  const targets = travellers.filter(user => user.id !== me?.id)
  const targetSelection = tripEmploymentSelection(travellers.map(user => user.id), me?.id, employment.employmentId, companyId, scope, targetChoices)
  const [seeded, setSeeded] = useState(false)
  // По умолчанию едет сам инициатор — как только профиль загрузился, сажаем его чипом.
  useEffect(() => {
    if (!seeded && me) {
      setTravellers([{ id: me.id, fullName: me.fullName, jobTitle: me.jobTitle }])
      setSeeded(true)
    }
  }, [me, seeded])

  // Направление из пикера (зеркало норм 1С): страна → strana_id (GUID), город + суточная норма.
  const [dest, setDest] = useState<TripDest | null>(null)
  // Полный адрес прибытия (v1.2 Блок 2) — уточнение + данные для трансфера, необязателен.
  const [address, setAddress] = useState('')
  const [purpose, setPurpose] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [dateReturn, setDateReturn] = useState('')
  const [comment, setComment] = useState('')

  const countryOk = !!dest?.stranaId
  const cityOk = !!dest?.city.trim()
  const datesOk = DATE_RE.test(dateFrom) && DATE_RE.test(dateTo) && dateTo >= dateFrom
  const returnOk = DATE_RE.test(dateReturn) && (!datesOk || dateReturn >= dateTo)
  const duration = datesOk ? daysInclusive(dateFrom, dateTo) : 0

  // Суточные с РАЗБИВКОЙ по каждому участнику + итог (ТЗ v1.2 Блок 3). Норма — зеркальная из
  // регистра 1С по выбранному городу; направление одно, поэтому ставка у всех одинаковая.
  // Пересчитывается сама при смене состава/дат/города. Свой город (нормы нет) → perDiem=0, блок скрыт.
  const perDiem = dest?.perDiem ?? 0
  const lines =
    perDiem > 0 && duration > 0
      ? travellers.map((m) => ({ id: m.id, name: m.fullName, amount: perDiem * duration }))
      : []
  const estTotal = lines.reduce((s, l) => s + l.amount, 0)
  // Чего не хватает — теми же проверками, что держат кнопку: подсказка не разойдётся с ней.
  const missing = [
    !employment.ready && t(MISSING_WORKPLACE),
    travellers.length === 0 && t({ ru: 'Кто едет', en: 'Travellers', az: 'Kim gedir' }),
    employment.ready && !targetSelection.ready && t({ ru: 'Места работы участников', en: 'Participant workplaces', az: 'İştirakçıların iş yerləri' }),
    !countryOk && t({ ru: 'Страна', en: 'Country', az: 'Ölkə' }),
    !cityOk && t({ ru: 'Город', en: 'City', az: 'Şəhər' }),
    purpose.trim() === '' && t({ ru: 'Цель командировки', en: 'Trip purpose', az: 'Ezamiyyətin məqsədi' }),
    !DATE_RE.test(dateFrom) && t(MISSING_DATE_FROM),
    !DATE_RE.test(dateTo) && t(MISSING_DATE_TO),
    !DATE_RE.test(dateReturn) && t({ ru: 'Выход на работу', en: 'Back to work', az: 'İşə çıxış' }),
  ].filter((x): x is string => !!x)
  const ok = missing.length === 0 && datesOk && returnOk && purpose.length <= 1000 &&
    comment.length <= 1000 && address.length <= 500 && !create.isPending

  const submit = () =>
    ok &&
    dest &&
    create.mutate({
      employmentId: employment.employmentId,
      employeeIds: travellers.map((m) => m.id),
      employeeEmploymentIds: targetSelection.employeeEmploymentIds,
      destinationCountryCode: dest.stranaId, // GUID страны 1С (strana_id) — резолвер примет
      destinationCityName: dest.city.trim(),
      destinationAddress: address.trim() || undefined,
      purpose: purpose.trim(),
      dateFrom,
      dateTo,
      dateOfReturnToWork: dateReturn,
      comment: comment.trim() || undefined,
    })

  // Первая нарушенная клиентская проверка → подсказка (серверный message главнее при сабмите).
  const hint =
    datesOk && DATE_RE.test(dateReturn) && dateReturn < dateTo
      ? t({ ru: 'Дата выхода на работу не может быть раньше окончания поездки', en: 'Return-to-work date cannot precede the trip end' })
      : ''

  return (
    <YStack gap={14}>
      <EmploymentField selection={employment} />
      {/* Суммы не вводятся: суточные/средний считает 1С (контракт 08.07.2026) */}
      <XStack
        alignItems="center"
        gap={8}
        backgroundColor="$primaryBg"
        borderWidth={1}
        borderColor="$primary"
        borderRadius={7}
        paddingHorizontal={12}
        paddingVertical={9}
      >
        <Icon name="info" size={15} color="$primary" />
        <Text fontSize={12} color="$color" flex={1}>
          {t({
            ru: 'Суточные показаны по нормам 1С — суммы вводить не нужно. Окончательный расчёт делают бухгалтер и CFO.',
            en: 'Per-diem is shown from the 1C norms — no amounts to enter. Final figures are set by Accounting and the CFO.',
          })}
        </Text>
      </XStack>

      <Field label={t({ ru: 'Кто едет', en: 'Travellers' })} required>
        <TripMatesPicker travellers={travellers} onChange={setTravellers} />
      </Field>

      {companyId && targets.map(user => <TripEmploymentField key={`${scope}:${user.id}`} user={user} companyId={companyId} onSelection={choice => setTargetChoices(current => {
        const old = current[user.id]
        if (old?.scope === scope && old.ready === choice.ready && old.id === choice.id && old.requiresChoice === choice.requiresChoice) return current
        return { ...current, [user.id]: { ...choice, scope } }
      })} />)}
      {employment.ready && !targetSelection.ready && <Text color="$muted">{t({ ru: 'Для выбранного состава не удалось подтвердить все места работы. Обратитесь в HR или измените состав участников.', az: 'Bütün iştirakçıların iş yerləri təsdiqlənmədi. HR ilə əlaqə saxlayın.', en: 'Not all participant workplaces could be verified. Contact HR or change participants.' })}</Text>}
      {/* направление: страна/город из зеркала норм 1С (пикер шлёт strana_id) */}
      <Field label={t({ ru: 'Направление (страна · город)', en: 'Destination (country · city)' })} required>
        <TripDestinationPicker value={dest} onChange={setDest} />
      </Field>

      {/* полный адрес прибытия (v1.2 Блок 2): уточнение города + данные для трансфера */}
      <Field label={t({ ru: 'Полный адрес прибытия (необязательно)', en: 'Full arrival address (optional)' })}>
        <FormTextArea
          value={address}
          onChangeText={setAddress}
          maxLength={500}
          placeholder={t({
            ru: 'Город / область / район / улица — если нужного города нет в списке или нужен трансфер',
            en: 'City / region / district / street — if the city is not listed or a transfer is needed',
          })}
          minHeight={52}
        />
      </Field>

      <Field label={t({ ru: 'Цель командировки', en: 'Trip purpose' })} required>
        <FormTextArea
          value={purpose}
          onChangeText={setPurpose}
          maxLength={1000}
          placeholder={t({ ru: 'Например: деловые переговоры с партнёром', en: 'e.g. business negotiations with a partner' })}
        />
      </Field>

      {/* даты: поездка с/по + выход на работу (требование контракта 1С) */}
      <XStack gap={12} flexWrap="wrap">
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'С', en: 'From' })} required>
            <DateField
              value={dateFrom}
              onChange={(d) => {
                setDateFrom(d)
                if (dateTo && dateTo < d) setDateTo('')
              }}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateFrom}
            />
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'По', en: 'To' })} required>
            <DateField
              value={dateTo}
              onChange={(d) => {
                setDateTo(d)
                // Дата выхода по умолчанию — следующий день после окончания (можно поменять).
                if (!dateReturn || dateReturn < d) setDateReturn(ymdPlusDays(d, 1))
              }}
              min={dateFrom || undefined}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateTo}
            />
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'Выход на работу', en: 'Back to work' })} required>
            <DateField
              value={dateReturn}
              onChange={setDateReturn}
              min={dateTo || undefined}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateReturn}
            />
          </Field>
        </YStack>
      </XStack>

      {duration > 0 && (
        <XStack alignItems="center" gap={7}>
          <Icon name="calendar" size={14} color="$muted" />
          <Text fontSize={12} fontWeight="600" color="$muted">
            {t({ ru: `Длительность: ${duration} дн.`, en: `Duration: ${duration} d`, az: `Müddət: ${duration} gün` })}
            {travellers.length > 1
              ? t({ ru: ` · групповая (${travellers.length} чел.)`, en: ` · group (${travellers.length} ppl)`, az: ` · qrup (${travellers.length} nəfər)` })
              : ''}
          </Text>
        </XStack>
      )}

      {/* суточные: разбивка по каждому участнику + ИТОГО (ТЗ v1.2 Блок 3, §4.2) */}
      {lines.length > 0 && dest && (
        <YStack
          gap={7}
          backgroundColor="$bgLayout"
          borderWidth={1}
          borderColor="$borderColor"
          borderRadius={7}
          paddingHorizontal={12}
          paddingVertical={10}
        >
          <XStack alignItems="center" gap={7}>
            <Icon name="wallet" size={15} color="$primary" />
            <Text fontSize={12} fontWeight="700" color="$color" flex={1}>
              {t({ ru: 'Суточные', en: 'Per-diem' })}
            </Text>
            <Text fontSize={11} color="$muted">
              {t({
                ru: `${dest.perDiem} ${dest.currency}/сут (норма 1С) × ${duration} дн.`,
                en: `${dest.perDiem} ${dest.currency}/day (1C norm) × ${duration} d`,
                az: `${dest.perDiem} ${dest.currency}/gün (1С norması) × ${duration} gün`,
              })}
            </Text>
          </XStack>

          {lines.map((l) => (
            <XStack key={l.id} alignItems="center" gap={8}>
              <Text fontSize={12} color="$muted" flex={1} numberOfLines={1}>
                {l.name}
              </Text>
              <Text fontSize={12} fontWeight="600" color="$color">
                {`${l.amount.toLocaleString('ru-RU')} ${dest.currency}`}
              </Text>
            </XStack>
          ))}

          <XStack
            alignItems="center"
            gap={8}
            borderTopWidth={1}
            borderColor="$borderColor"
            paddingTop={7}
          >
            <Text fontSize={12} fontWeight="700" color="$color" flex={1}>
              {t({ ru: 'ИТОГО', en: 'TOTAL' })}
            </Text>
            <Text fontSize={13} fontWeight="700" color="$primary">
              {`${estTotal.toLocaleString('ru-RU')} ${dest.currency}`}
            </Text>
          </XStack>

          <Text fontSize={11} color="$muted">
            {t({
              ru: 'Предварительно; окончательные суммы — у бухгалтера и CFO',
              en: 'Preliminary; final figures are set by Accounting and the CFO',
            })}
          </Text>
        </YStack>
      )}

      <Field label={t({ ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}>
        <FormTextArea
          value={comment}
          onChangeText={setComment}
          maxLength={1000}
          placeholder={t({ ru: 'Например: встреча с подрядчиком', en: 'e.g. contractor meeting' })}
        />
      </Field>

      {hint !== '' && (
        <Text fontSize={12} color={tone('red').text}>
          {hint}
        </Text>
      )}

      <RoutePreview type="BUSINESS_TRIP" employmentId={employment.employmentId} enabled={employment.ready} />
      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}
